import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/compat/router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getOrganizationAdminsMeAuthorities } from 'apis/organization_admins/me/authorities';
import { authoritiesAtom, currentOrganizationAdminAtom } from './atoms';

export const useTargetResource = (): string => {
  const router = useRouter();
  if (!router?.isReady) return '';

  return router.pathname.split('/')[1];
};

const getOrganizationAdminsAuthorities = async (): Promise<
  Map<string, boolean>
> => {
  let authMap = new Map<string, boolean>([]);

  const res = await getOrganizationAdminsMeAuthorities();
  if (res.status === 200 && res.data) {
    authMap = new Map<string, boolean>(Object.entries(res.data));
  }

  return authMap;
};

export const useAuthorities = (): Map<string, boolean> => {
  const [authorities, setAuthorities] = useAtom(authoritiesAtom);
  const currentOrganizationAdmin = useAtomValue(currentOrganizationAdminAtom);
  const [authoritiesMap, setAuthoritiesMap] = useState<Map<string, boolean>>(
    new Map<string, boolean>([])
  );
  const apiCalledRef = useRef(false);

  useEffect(() => {
    // See: https://github.com/reactwg/react-18/discussions/18#discussion-3385714
    if (apiCalledRef.current === false && currentOrganizationAdmin.id) {
      apiCalledRef.current = true;
      if (authorities) {
        setAuthoritiesMap(authorities);
      } else {
        getOrganizationAdminsAuthorities().then((authMap) => {
          setAuthoritiesMap(authMap);
          setAuthorities(authMap);
        });
      }
    }
  }, [
    authorities,
    setAuthorities,
    authoritiesMap,
    setAuthoritiesMap,
    currentOrganizationAdmin,
  ]);

  return authoritiesMap;
};

export const useCheckAuthorities = (
  resource: string,
  action:
    | 'Read'
    | 'Update'
    | 'Create'
    | 'Delete'
    | 'UpdateAccessibility'
    | 'Devolve'
    | 'EmailsBulkUpdate',
  redirectPath: string
) => {
  const router = useRouter();
  const authorities = useAuthorities();
  const authoritiesKey = `${snakeToCamel(resource)}${action}`;

  useEffect(() => {
    if (authorities instanceof Map) {
      const availableAction = authorities.get(authoritiesKey);
      if (availableAction !== undefined && !availableAction) {
        toast.warn('アクセス権限がありません', { delay: 500 });
        router?.push(redirectPath);
      }
    }
  }, [router, redirectPath, authorities, authoritiesKey]);
};

export const isAvailableAction = (
  authorities: Map<string, boolean>,
  resource: string,
  action:
    | 'Read'
    | 'Update'
    | 'Create'
    | 'Delete'
    | 'UpdateAccessibility'
    | 'Devolve'
    | 'EmailsBulkUpdate'
): boolean => {
  const authoritiesKey = `${snakeToCamel(resource)}${action}`;
  return authorities?.get(authoritiesKey) || false;
};

export const checkAuthorities = async (
  resource: string,
  action:
    | 'Read'
    | 'Update'
    | 'Create'
    | 'Delete'
    | 'UpdateAccessibility'
    | 'Devolve'
    | 'EmailsBulkUpdate'
): Promise<boolean> => {
  const authoritiesKey = `${snakeToCamel(resource)}${action}`;
  const authMap = await getOrganizationAdminsAuthorities();
  return authMap?.get(authoritiesKey) || false;
};

const snakeToCamel = (snakeCase: string): string => {
  return snakeCase.replace(/_[a-z]/g, (match) =>
    match.toUpperCase().replace('_', '')
  );
};
